import { useDeepCompare } from '@anchorage/hooks';
import { handleError } from '@anchorage/sentry';
import type { UploadedFile } from '@latitude/file-upload';
import type { reactHookForm } from '@latitude/form';
import { FormFileUpload, useFormContext } from '@latitude/form';
import { useFileUpload } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import type { FormFileUploadWithProgressProps } from 'types';

type FormFieldValue<T extends reactHookForm.FieldValues> =
  reactHookForm.PathValue<T, reactHookForm.Path<T>>;

export const FormFileUploadWithProgress = <
  T extends reactHookForm.FieldValues = reactHookForm.FieldValues,
>({
  name,
  // right now, this component is used only for direct API upload, once this becames more generic, we need to
  // make this prop more dynamic
  directUploadToApi = true,
  uploadURL,
  uploadFileName,
  onUploadComplete,
  onUploadError,
  onChange,
  fileURLs,
  fileFetchHeaders,
  ...restProps
}: FormFileUploadWithProgressProps<T>) => {
  const { uploadFile } = useFileUpload();
  const [files, setFiles] = useState([] as UploadedFile[]);
  const { setValue } = useFormContext<T>();
  const objectURLs = useRef<string[]>([]);

  const setFormFiles = (newFiles: UploadedFile[]) => {
    setValue(name, newFiles as FormFieldValue<T>, { shouldValidate: true });
  };

  // read the initial fileURLs and set the files
  useEffect(
    () => {
      const controller = new AbortController();
      fileURLs?.forEach((fileURL) => {
        let gotResponse = false;
        fetch(fileURL, {
          credentials: 'include',
          signal: controller.signal,
          ...(fileFetchHeaders && { headers: fileFetchHeaders }),
        })
          .then((response) => {
            gotResponse = true;
            if (!response.ok) {
              throw new Error(
                `File request failed with status ${response.status}`,
              );
            }
            return response.blob();
          })
          .then((blob) => {
            // A fetch that outlives this effect run (unmount or newer
            // fileURLs) must not create an object URL cleanup can't see or
            // clobber newer files.
            if (controller.signal.aborted) {
              return;
            }
            // The file name renders as an <a target="_blank">, and browser
            // navigations cannot carry fileFetchHeaders (or reuse this fetch's
            // credentials), so link the already-fetched bytes instead of the
            // server URL. blob.type must be preserved or the new tab downloads
            // the file instead of rendering it.
            const objectURL = URL.createObjectURL(blob);
            objectURLs.current.push(objectURL);
            const file: UploadedFile = {
              file: new File([blob], uploadFileName, { type: blob.type }),
              name: uploadFileName,
              url: objectURL,
            };
            setFiles([file]);
            setFormFiles([file]);
          })
          .catch((error) => {
            // Abort rejections and late failures are not user-facing errors
            if (controller.signal.aborted) {
              return;
            }
            // No response at all (offline, cancelled by a navigation) is not
            // actionable, and once wrapped it slips past the global
            // 'Failed to fetch' Sentry filter.
            if (!gotResponse) {
              return;
            }
            // A constant message keeps every field in one Sentry issue
            handleError(
              new Error(
                'Unable to retrieve file',
                // oxlint-disable-next-line @typescript-eslint/no-unsafe-argument
                { cause: error },
              ),
              { extraContext: { fieldName: name } },
            );
          });
      });
      return () => {
        controller.abort();
      };
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompare([name, uploadFileName, fileURLs, fileFetchHeaders]),
  );

  // Object URLs hold their blobs in memory until explicitly revoked
  useEffect(
    () => () => {
      objectURLs.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const handleOnComplete = (
    completedFile: UploadedFile,
    response: XMLHttpRequest['response'],
    error?: Error,
  ) => {
    // update the file list with the completed file
    const updatedFiles = files?.length
      ? files.map((file) =>
          file.name === completedFile.name ? completedFile : file,
        )
      : [completedFile];
    setFiles(updatedFiles);
    setFormFiles(updatedFiles);

    if (error) {
      onUploadError?.(completedFile, error);
    } else {
      onUploadComplete?.(completedFile, response);
    }
  };

  const handleFileChange = (currentFiles: UploadedFile[]) => {
    // Call the original onChange if provided
    onChange?.(currentFiles);
    setFiles(currentFiles);
    setFormFiles(currentFiles);

    // Start uploading each new file
    currentFiles.forEach((newFile) => {
      if (newFile.file) {
        uploadFile(newFile, uploadURL, uploadFileName, handleOnComplete);
      }
    });
  };

  return (
    <FormFileUpload<T>
      {...restProps}
      directUploadToApi={directUploadToApi}
      name={name}
      onChange={handleFileChange}
    />
  );
};
